import React, { useCallback, useMemo, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import type { EventClickArg, EventInput } from "@fullcalendar/core";
import allLocales from "@fullcalendar/core/locales-all";
import { CalendarAppointment } from "../../types/appointment";
import { AddGenericModal } from "../../Modals/AddGenericModal";
import { PatientDataEntryForm } from "../../Components/PatientDataEntryForm";

interface CalendarViewProps {
  events: CalendarAppointment[];
}

type SelectedAppointment = {
  patientId: number;
  patientName: string;
  appointmentId?: string;
  start?: string;
  end?: string;
};

const CalendarView: React.FC<CalendarViewProps> = ({ events }) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selected, setSelected] = useState<SelectedAppointment | null>(null);

  const calendarEvents: EventInput[] = useMemo(
    () =>
      events.map((app) => ({
        id: app.id,
        title: app.title,
        start: app.start,
        end: app.end,
        extendedProps: {
          patientId: app.encounterData?.patientId,
          patientName: app.patientName,
          amka: app.amka,
          encounterData: app.encounterData,
        },
      })),
    [events],
  );

  const closeForm = useCallback(() => {
    setIsFormOpen(false);
    setSelected(null);
  }, []);

  const handleEventClick = useCallback((info: EventClickArg) => {
    const { event } = info;
    const props = event.extendedProps as {
      patientId?: number | string;
      patientName?: string;
      encounterData?: { patientId?: number | string };
    };

    const rawPatientId =
      props.patientId ?? props.encounterData?.patientId ?? null;
    const patientId = Number(rawPatientId);

    if (!rawPatientId || Number.isNaN(patientId) || patientId <= 0) {
      console.warn(
        "[CalendarView] Δεν βρέθηκε έγκυρο patientId στο event:",
        event.id,
        props,
      );
      return;
    }

    setSelected({
      patientId,
      patientName: props.patientName || event.title || `Ασθενής #${patientId}`,
      appointmentId: event.id,
      start: event.start?.toISOString(),
      end: event.end?.toISOString(),
    });
    setIsFormOpen(true);
  }, []);

  const modalTitle = selected
    ? `Καταχώρηση δεδομένων — ${selected.patientName}`
    : "Καταχώρηση δεδομένων ασθενούς";

  return (
    <>
      <FullCalendar
        locales={allLocales}
        locale="el"
        plugins={[dayGridPlugin, timeGridPlugin]}
        initialView="dayGridMonth"
        headerToolbar={{
          left: "prev,next today",
          center: "title",
          right: "dayGridMonth,timeGridWeek,timeGridDay",
        }}
        height="100%"
        events={calendarEvents}
        eventClick={handleEventClick}
        eventClassNames="cursor-pointer"
      />

      <AddGenericModal
        isOpen={isFormOpen && selected !== null}
        onClose={closeForm}
        title={modalTitle}
        size="xl"
      >
        {selected ? (
          <PatientDataEntryForm
            key={selected.patientId}
            patientId={selected.patientId}
            title={`Ιατρικά δεδομένα — ${selected.patientName}`}
            defaultOpenSection={null}
          />
        ) : null}
      </AddGenericModal>
    </>
  );
};

export default CalendarView;
